import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import { ChatDockProvider } from "@/components/ChatDock";
import { Loading } from "@/components/Shared";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Landing from "@/pages/Landing";
import { Terms, Privacy } from "@/pages/Legal";
import Leads from "@/pages/Leads";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Analytics = lazy(() => import("@/pages/Analytics"));
import Products from "@/pages/Products";
import Inventory from "@/pages/Inventory";
const Field = lazy(() => import("@/pages/Field"));
const FieldVisitDetail = lazy(() => import("@/pages/FieldVisitDetail"));
import WarehouseDetail from "@/pages/WarehouseDetail";
import SupplierDetail from "@/pages/SupplierDetail";
import VendorPODetail from "@/pages/PurchaseOrderDetail";
import Expenses from "@/pages/Expenses";
import { AppProvider } from "@/context/AppContext";
import ProductDetail from "@/pages/ProductDetail";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
const Settings = lazy(() => import("@/pages/Settings"));
import Inbox from "@/pages/Inbox";
import Enquiries from "@/pages/Enquiries";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import EnquiryDetail from "@/pages/EnquiryDetail";
import Sales from "@/pages/Sales";
const QuoteBuilder = lazy(() => import("@/pages/QuoteBuilder"));
const ScanPage = lazy(() => import("@/pages/Scan"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const AiAgent = lazy(() => import("@/pages/AiAgent"));
const Slabs = lazy(() => import("@/pages/Slabs"));
const QuoteDetail = lazy(() => import("@/pages/QuoteDetail"));
import OrderDetail from "@/pages/OrderDetail";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PurchaseOrderDetail from "@/pages/PurchaseOrders";
import { PortalProvider, usePortal } from "@/portal/PortalContext";
import PortalLogin from "@/portal/PortalLogin";
import PortalLayout from "@/portal/PortalLayout";
import PortalCatalog from "@/portal/PortalCatalog";
import PortalCart from "@/portal/PortalCart";
import { PortalOrders, PortalOrderDetail, PortalInvoices } from "@/portal/PortalOrders";

function PortalProtected({ children }) {
  const { me, ready } = usePortal();
  if (!ready) return <div className="h-full flex items-center justify-center"><Loading label="Opening portal…" /></div>;
  if (!me) return <Navigate to="/portal/login" replace />;
  return <PortalLayout>{children}</PortalLayout>;
}

function PortalShell() {
  const { me, ready } = usePortal();
  return (
    <Routes>
      <Route path="/portal/login" element={ready && me ? <Navigate to="/portal" replace /> : <PortalLogin />} />
      <Route path="/portal" element={<PortalProtected><PortalCatalog /></PortalProtected>} />
      <Route path="/portal/cart" element={<PortalProtected><PortalCart /></PortalProtected>} />
      <Route path="/portal/orders" element={<PortalProtected><PortalOrders /></PortalProtected>} />
      <Route path="/portal/orders/:id" element={<PortalProtected><PortalOrderDetail /></PortalProtected>} />
      <Route path="/portal/invoices" element={<PortalProtected><PortalInvoices /></PortalProtected>} />
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="h-full flex items-center justify-center"><Loading label="Starting TradeDesk360…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

// Phones open on Tasks (calendar) the first time in a session; Home stays reachable via the drawer afterwards.
function MobileHome() {
  sessionStorage.setItem("td_home_seen", "1");
  return <Navigate to="/tasks" replace />;
}

function RootGate() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="h-full flex items-center justify-center"><Loading label="Starting TradeDesk360…" /></div>;
  if (!user) {
    if (window.location.hostname.startsWith("app.")) return <Navigate to="/login" replace />;
    return <Landing />;
  }
  const home = window.innerWidth < 768 && !sessionStorage.getItem("td_home_seen") ? <MobileHome /> : <Dashboard />;
  return <AppLayout>{home}</AppLayout>;
}

function Shell() {
  const { user, ready } = useAuth();
  return (
    <Suspense fallback={<div className="h-full flex items-center justify-center"><Loading /></div>}>
    <Routes>
      <Route path="/login" element={ready && user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/signup" element={ready && user ? <Navigate to="/" replace /> : <Signup />} />
      <Route path="/" element={<RootGate />} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/tasks/:id" element={<Protected><TaskDetail /></Protected>} />
      <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
      <Route path="/ai-agent" element={<Protected><AiAgent /></Protected>} />
      <Route path="/campaigns" element={<Protected><Campaigns /></Protected>} />
      <Route path="/enquiries" element={<Protected><Enquiries /></Protected>} />
      <Route path="/enquiries/:id" element={<Protected><EnquiryDetail /></Protected>} />
      <Route path="/sales" element={<Protected><Sales /></Protected>} />
      <Route path="/quotes/new" element={<Protected><QuoteBuilder /></Protected>} />
      <Route path="/quotes/:id/edit" element={<Protected><QuoteBuilder /></Protected>} />
      <Route path="/orders/new" element={<Protected><QuoteBuilder kind="order" /></Protected>} />
      <Route path="/invoices/new" element={<Protected><QuoteBuilder kind="invoice" /></Protected>} />
      <Route path="/scan" element={<Protected><ScanPage /></Protected>} />
      <Route path="/quotes/:id" element={<Protected><QuoteDetail /></Protected>} />
      <Route path="/orders/:id" element={<Protected><OrderDetail /></Protected>} />
      <Route path="/invoices/:id" element={<Protected><InvoiceDetail /></Protected>} />
      <Route path="/purchase-orders/:id" element={<Protected><PurchaseOrderDetail /></Protected>} />
      <Route path="/products" element={<Protected><Products /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/slabs" element={<Protected><Slabs /></Protected>} />
      <Route path="/field" element={<Protected><Field /></Protected>} />
      <Route path="/field/visits/:id" element={<Protected><FieldVisitDetail /></Protected>} />
      <Route path="/inventory/warehouses/:id" element={<Protected><WarehouseDetail /></Protected>} />
      <Route path="/inventory/suppliers/:id" element={<Protected><SupplierDetail /></Protected>} />
      <Route path="/inventory/purchase-orders/:id" element={<Protected><VendorPODetail /></Protected>} />
      <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
      <Route path="/products/:id" element={<Protected><ProductDetail /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  const isPortal = window.location.pathname.startsWith("/portal");
  return (
    <BrowserRouter>
      {isPortal ? (
        <PortalProvider>
          <PortalShell />
          <Toaster position="top-right" richColors />
        </PortalProvider>
      ) : (
      <AuthProvider>
        <AppProvider>
        <ChatDockProvider>
          <Shell />
          <Toaster position="top-right" richColors />
        </ChatDockProvider>
        </AppProvider>
      </AuthProvider>)}
    </BrowserRouter>
  );
}
